import { Static, Type } from '@sinclair/typebox';

// =====================================================================
// Hébergement email (Stalwart managé) — contrats partagés.
//
// v1 : le pro crée des boîtes sur SON domaine (contact@mon-salon.fr) + DNS auto,
// puis les branche sur son client mail (Gmail/Apple/Outlook). Pas de webmail.
// Cf. docs/to_implement/email-hosting-stalwart-api-implementation.md.
//
// ⚠️ Miroir des enums Prisma `ProMailDomainStatus` / `ProMailboxStatus`
// (packages/backend/prisma-pg/schema.prisma) — garder l'ordre et les valeurs synchro.
// =====================================================================

/** État du provisioning mail d'un domaine possédé par le pro (côté Stalwart + DNS). */
export enum ProMailDomainStatus {
  PENDING = 'PENDING',
  PROVISIONING = 'PROVISIONING',
  ACTIVE = 'ACTIVE',
  ERROR = 'ERROR',
  DISABLED = 'DISABLED',
}

/** État d'une boîte mail hébergée chez Stalwart. */
export enum ProMailboxStatus {
  PENDING = 'PENDING',
  PROVISIONING = 'PROVISIONING',
  ACTIVE = 'ACTIVE',
  ERROR = 'ERROR',
  SUSPENDED = 'SUSPENDED',
}

const enumLiteralUnion = (e: Record<string, string>): ReturnType<typeof Type.Union> =>
  Type.Union(Object.values(e).map((v): ReturnType<typeof Type.Literal> => Type.Literal(v)));

/**
 * Partie locale d'une adresse (avant le @). Minuscules, chiffres, `.`, `_`, `-`,
 * ne commence/finit pas par un séparateur. Devient `x:Account.name` côté Stalwart.
 */
export const MAILBOX_LOCAL_PART_PATTERN = '^[a-z0-9]([a-z0-9._-]*[a-z0-9])?$';

/**
 * Bornes du quota par boîte (octets). Le maximum est une garde ANTI-ABUS : sans lui, le pro
 * pourrait se provisionner une boîte de plusieurs To et contourner le plan (Stalwart ne borne
 * rien de lui-même). Le minimum évite la valeur `0` que Stalwart interprète comme « illimité ».
 */
export const MAILBOX_MIN_QUOTA_BYTES = 1024 ** 2; // 1 Mio
export const MAILBOX_MAX_QUOTA_BYTES = 50 * 1024 ** 3; // 50 Gio

/**
 * Bornes d'un mot de passe de boîte CHOISI par le pro (le mot de passe généré par défaut n'y est
 * pas soumis). 12 minimum : les boîtes partagent l'infra Stalwart de tous les pros — une boîte
 * compromise qui spamme dégrade la réputation IP de toute la plateforme.
 */
export const MAILBOX_PASSWORD_MIN_LENGTH = 12;
export const MAILBOX_PASSWORD_MAX_LENGTH = 128;

// ── Requêtes ──

/** POST /app/email-hosting/mailboxes — créer une boîte sur un domaine possédé par le pro. */
export const CreateMailboxRequestSchema = Type.Object({
  /** Domaine cible (doit appartenir au pro via `pro_website_domain`). */
  domain: Type.String({ minLength: 3, maxLength: 255, format: 'hostname' }),
  /** Partie locale (avant le @). */
  local_part: Type.String({
    minLength: 1,
    maxLength: 64,
    pattern: MAILBOX_LOCAL_PART_PATTERN,
  }),
  /** Nom d'affichage optionnel (From). */
  display_name: Type.Optional(Type.String({ maxLength: 255 })),
  /** Quota en octets. Absent = quota par défaut du plan. Borné anti-abus (cf. MAILBOX_*_QUOTA_BYTES). */
  quota_bytes: Type.Optional(Type.Integer({ minimum: MAILBOX_MIN_QUOTA_BYTES, maximum: MAILBOX_MAX_QUOTA_BYTES })),
  /**
   * Mot de passe choisi par le pro. Absent = passphrase générée côté backend (affichée une fois).
   * Jamais stocké côté Propelsia — poussé à Stalwart uniquement.
   */
  password: Type.Optional(
    Type.String({
      minLength: MAILBOX_PASSWORD_MIN_LENGTH,
      maxLength: MAILBOX_PASSWORD_MAX_LENGTH,
      meta: { input_type: 'password' },
    }),
  ),
});
export type CreateMailboxRequest = Static<typeof CreateMailboxRequestSchema>;

/** POST /app/email-hosting/mailboxes/:id/password — définir un mot de passe choisi par le pro. */
export const SetMailboxPasswordRequestSchema = Type.Object({
  password: Type.String({
    minLength: MAILBOX_PASSWORD_MIN_LENGTH,
    maxLength: MAILBOX_PASSWORD_MAX_LENGTH,
    meta: { input_type: 'password' },
  }),
});
export type SetMailboxPasswordRequest = Static<typeof SetMailboxPasswordRequestSchema>;

// ── Réponses ──

/**
 * Paramètres de connexion IMAP/SMTP à afficher en permanence (panneau « Réglages mail »).
 * Hôtes/ports statiques de la plateforme (hôte mail partagé `*.propelsia.ai`).
 */
export const MailConnectionSettingsSchema = Type.Object({
  imap_host: Type.String(),
  imap_port: Type.Number(),
  smtp_host: Type.String(),
  smtp_port: Type.Number(),
  /** Identifiant = adresse email complète. */
  username: Type.String(),
});
export type MailConnectionSettings = Static<typeof MailConnectionSettingsSchema>;

/** Une boîte mail telle qu'affichée dans la liste. Aucun mot de passe ici. */
export const MailboxSchema = Type.Object({
  pro_mailbox_id: Type.Number(),
  email: Type.String(),
  local_part: Type.String(),
  domain: Type.String(),
  display_name: Type.Optional(Type.String()),
  status: enumLiteralUnion(ProMailboxStatus),
  /** Quota formaté prêt à afficher (ex. « 5 Go »), ou vide si défaut. */
  quota_formatted: Type.Optional(Type.String()),
  /**
   * Boîte héritée, reprise d'une offre mail OVH par la migration (acquise, hors quota du plan).
   * Cf. docs/to_implement/email-ovh-exchange-migration-plan.md §5.4.
   */
  legacy: Type.Optional(Type.Boolean()),
});
export type Mailbox = Static<typeof MailboxSchema>;

/**
 * Réponse de création (ou de régénération de mot de passe). Le mot de passe est
 * généré côté backend, poussé à Stalwart, et renvoyé UNE SEULE FOIS ici (jamais stocké).
 */
export const MailboxWithPasswordSchema = Type.Object({
  mailbox: MailboxSchema,
  /** Mot de passe en clair — affiché une fois, non stocké, régénérable. */
  password: Type.String(),
  settings: MailConnectionSettingsSchema,
});
export type MailboxWithPassword = Static<typeof MailboxWithPasswordSchema>;

/** GET /app/email-hosting/mailboxes — liste des boîtes du pro + réglages de connexion. */
export const ListMailboxesResponseSchema = Type.Object({
  mailboxes: Type.Array(MailboxSchema),
  settings: MailConnectionSettingsSchema,
  /** Nombre de boîtes restantes autorisées par le plan (null = illimité). Toujours présent. */
  remaining_quota: Type.Union([Type.Integer(), Type.Null()]),
});
export type ListMailboxesResponse = Static<typeof ListMailboxesResponseSchema>;
